/** biome-ignore-all lint/nursery/useUniqueElementIds: <explanation> */
import type { AdministratingHeaderProps } from "senf-atomic-design-system/src/components/molecules/headers/AdministratingHeader.types";
import "../../organisms/datePicker/init";
import "react-dates/initialize";
import "senf-atomic-design-system/src/components/organisms/datePicker/react-dates.styles.css";
import { DayPickerSingleDateController } from "react-dates";

import "react-dates/lib/css/_datepicker.css";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { type FormikProps, useFormik } from "formik";
import type { Map as MapType } from "mapbox-gl";
import type { Moment } from "moment";
import moment from "moment/min/moment-with-locales";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { useParams } from "react-router-dom";
import {
  AdministratingHeader,
  Geocoder,
  Icon,
  InfoToast,
  InvisibleInput,
  ListItem,
  SettingsModal,
  ShareModal,
  Transition,
  useDiscardModal,
  useModals,
  useOnClickOutside,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import FileDrop from "senf-atomic-design-system/src/components/core/fileDrop/FileDrop";
import {
  SortableSections,
  SortableWrapper,
} from "senf-atomic-design-system/src/components/special/pageBuilder/SortableSections";
import { useVerifyFiles } from "senf-atomic-design-system/src/components/special/survey/legacy/hooks";
import { cn } from "senf-atomic-design-system/src/util/classNameConstructor";
import {
  capitalizeFirstLetter,
  formatBytes,
  getMediaObjects,
  useConvertCase,
  useGetFireStoreSizes,
  useGlobalState,
} from "senf-shared";
import type { CalendarEvent } from "senf-shared/calendarEvent";
import type {
  Project,
  SectionButton,
  SectionCalendar,
  SectionFAQ,
  SectionFile,
  SectionImage,
  SectionMap,
  SectionText,
  SectionTimeline,
} from "senf-shared/types";
import { getFileTypeLabel } from "senf-shared/utils/helpers";
import i18n from "senf-shared/utils/i18n";
import { v4 as uuidv4 } from "uuid";
import { useImageMetaData } from "../../../hooks/useImageMetaData";
import MapViewEditor from "../../atoms/map/MapViewEditor";
import MapDatasetsList from "../../atoms/map/mapBuilder/MapDatasetsList";
import Dropdown from "../../core/dropdown/Dropdown";
import { Input } from "../../core/inputs/Input";
import TruncateMiddle from "../../core/TruncateMiddle";
import Toast from "../../core/toast/Toast";
import ImagesUploadModal, {
  type ImageInput,
} from "../imageModifier/ImagesUploadModal";
import ModifyImageButtons from "../imageModifier/ModifyImageButtons";
import EditableContent, {
  type EditableContentProps,
} from "../survey/components/EditableContent";
import SortableLogos from "../survey/components/SortableLogos";
import type { usePageBuilderFormik } from "./hooks";
import { InfoPageStyles, useGetImageRatio } from "./InfoPage";
import PreviewInfoPage from "./PreviewInfoPage";

interface HeaderProps {
  mutatableMode: NonNullable<AdministratingHeaderProps["mutatableMode"]>;
  unsavedChanges: boolean;
  handlePublish: (e: any) => Promise<void>;
  previewLink?: string;
  formik: ReturnType<typeof usePageBuilderFormik>;
  project: Project;
  handleButtonClose: () => void;
  handleDeleteInfoPage: () => void;
}
export const Header = ({
  previewLink,
  formik,
  handleButtonClose,
  handleDeleteInfoPage,
  handleSetHistory,
  project,
  ...props
}: HeaderProps) => {
  const { t } = useTranslation();
  const { openModal, closeModal } = useModals();
  const [_, setTranslation] = useGlobalState().translationsState;

  const handleShowSettings = () => {
    openModal(
      <SettingsModal
        type="infoPage"
        handleDelete={handleDeleteInfoPage}
        formik={formik}
        variant="settings"
      />,
      {
        title: t("global:settings"),
      },
    );
  };
  const handleShowShare = previewLink
    ? () =>
        openModal(
          <ShareModal
            previewLink={previewLink}
            translationKeys={{
              subTitle: t("infoPage:builder.shareModal.publicLink.subTitle"),
            }}
            dataTitle={t("global:infoPage")}
          />,
          {
            title: t("util:share", { val: t("global:infoPage") }),
            zIndex: 999,
            id: "shareInfoPage",
            size: "lg",
          },
        )
    : undefined;
  const languageBefore = useRef<string>();
  useEffect(() => {
    languageBefore.current = i18n.resolvedLanguage;
  }, []);

  const [showPreview, setShowPreview] = useState(false);

  return (
    <>
      <PreviewInfoPage
        project={project}
        formik={formik}
        onClose={() => setShowPreview(false)}
        show={showPreview}
      />
      <AdministratingHeader
        {...props}
        type="infoPage"
        id="infoPage"
        administratingTypeLabel={
          props.mutatableMode.current === "edit"
            ? t("util:create", { val: t("global:projectInfoPage") })
            : t("util:edit", { val: t("global:projectInfoPage") })
        }
        handleButtonClose={handleButtonClose}
        handleShowSettings={handleShowSettings}
        handleShowShare={handleShowShare}
        handleShowPreview={() =>
          previewLink ? setShowPreview(!showPreview) : undefined
        }
      />
    </>
  );
};
interface SectionProps<T> {
  item: T;
  formik: FormikProps<Project>;
  index: number;
}

// Hook to provide memoized field change handlers
const useOptimizedFormik = (formik: FormikProps<Project>) => {
  const setFieldValue = useCallback(
    (path: string, value: string | number | boolean | null) => {
      formik.setFieldValue(path, value);
    },
    [formik],
  );

  return {
    setFieldValue,
    values: formik.values,
  };
};

// Optimized field wrapper that only rerenders when its specific value changes
const OptimizedInvisibleInput = memo(
  ({
    fieldPath,
    value,
    onFieldChange,
    ...props
  }: EditableContentProps & {
    fieldPath: string;
    onFieldChange: (path: string, value: string) => void;
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const debounceRef = useRef<NodeJS.Timeout>();

    // Update local value when prop value changes (external updates)
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        // Debounce formik updates to reduce rerenders
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onFieldChange(fieldPath, newValue);
        }, 50);
      },
      [fieldPath, onFieldChange],
    );

    const handleBlur = useCallback(() => {
      // Ensure final value is synced on blur
      clearTimeout(debounceRef.current);
      onFieldChange(fieldPath, localValue);
    }, [fieldPath, localValue, onFieldChange]);

    return (
      <EditableContent
        {...props}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  },
);

// Optimized Input wrapper that debounces formik updates
const OptimizedInput = memo(
  ({
    fieldPath,
    value,
    onFieldChange,
    ...props
  }: {
    fieldPath: string;
    value: string | undefined;
    onFieldChange: (path: string, value: string) => void;
    [key: string]: any;
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const debounceRef = useRef<NodeJS.Timeout>();

    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        setLocalValue(newValue);

        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          onFieldChange(fieldPath, newValue);
        }, 50);
      },
      [fieldPath, onFieldChange],
    );

    const handleBlur = useCallback(() => {
      clearTimeout(debounceRef.current);
      onFieldChange(fieldPath, localValue);
    }, [fieldPath, localValue, onFieldChange]);

    return (
      <Input
        {...props}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  },
);

const TextSection = memo(
  ({
    item,
    formik,
    index,
    defaultTitle,
    defaultBody,
    withBody = true,
    rows = 5,
  }: {
    defaultTitle?: string;
    defaultBody?: string;
    withBody?: boolean;
    rows?: number;
  } & SectionProps<SectionText>) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(formik);

    return (
      <>
        <OptimizedInvisibleInput
          className="heading-2xl"
          fieldPath={`content[${index}].title`}
          value={item.title || ""}
          defaultValue={defaultTitle}
          onFieldChange={setFieldValue}
          placeholder={t("global:headline")}
        />
        {withBody && (
          <OptimizedInvisibleInput
            id={item?.uid}
            className="body-md"
            fieldPath={`content[${index}].body`}
            value={item.body || ""}
            defaultValue={defaultBody}
            onFieldChange={setFieldValue}
            placeholder={t("global:summary")}
            rows={rows}
            maxRows={Number.POSITIVE_INFINITY}
            rich
          />
        )}
      </>
    );
  },
);

const MapSection = ({
  item,
  formik,
  index,
  project,
  setProject,
  mapEditorActive,
  setMapEditorActive,
}) => {
  const { t } = useTranslation();
  const [statefulMap, setStatefulMap] = useState<MapType>();
  const [showMapDataSetList, setShowMapDataSetList] = useState<boolean>(false);
  const mapData =
    item?.mapId && item?.mapId !== ""
      ? project?.maps?.find((map) => map.mapId === item?.mapId)
      : null;
  const flyToDependencies = useMemo(
    () => [index, item?.uid, item?.mapData],
    [index, item?.uid],
  );
  if (!project) return;
  return (
    <div className="relative flex flex-col gap-3">
      <TextSection
        item={{
          ...item,
          type: "text",
        }}
        defaultTitle={t("infoPage:faq.title")}
        defaultBody={t("infoPage:faq.description")}
        formik={formik}
        index={index}
        rows={1}
      />
      <div className="h-[500px] relative">
        {mapEditorActive === item?.uid ? (
          <MapViewEditor
            mapSettings={item?.mapSettings}
            setMapSettings={(mapSettings) => {
              formik.setFieldValue(
                `content[${index}].mapSettings`,
                mapSettings,
              );
            }}
            mapData={mapData}
            resizeDependencies={[...flyToDependencies, isMobile]}
            flyToDependencies={flyToDependencies}
            mode="edit"
            statefulMap={statefulMap}
            setStatefulMap={setStatefulMap}
            setShowMapDataSetList={setShowMapDataSetList}
            shouldFlyTo={item?.mapSettings}
          />
        ) : (
          <div className="h-[500px]">
            <div className="h-full w-full bg-gray-200 justify-center flex items-center flex-col gap-5">
              <p>Map Editor is not active</p>
              <Button
                onClick={() => {
                  setMapEditorActive(item?.uid);
                }}
              >
                Edit Map
              </Button>
            </div>
          </div>
        )}

        <MapDatasetsList
          handleUpload={(mapId) => {
            formik.setFieldValue(`content[${index}].mapId`, mapId);
          }}
          selectedMapId={item?.mapId}
          project={project}
          setProject={setProject}
          show={showMapDataSetList}
          setShow={setShowMapDataSetList}
          mapId={item?.mapId}
        />
      </div>
      {mapEditorActive && (
        <Button
          variant="basic"
          leadingIcon="edit"
          text={t("util:edit", { val: t("global:map") })}
          onClick={setShowMapDataSetList}
        />
      )}
    </div>
  );
};

const LogoSection = ({ item, formik, index }) => {
  const { organizationId, projectId } = useParams();
  const [loading, setLoading] = useState(false);

  const filePath = `organizationsData/${organizationId}/projects/${projectId}`;

  const [logos, setLogosState] = useState<Logo[]>(item?.images || []);

  const addUid = (e) => ({
    ...e,
    uid: e.uid || uuidv4(),
  });
  const addLogos = useCallback(
    async (value) => {
      const logoArray: typeof logos = [];

      if (Array.isArray(value) && value[0] && "url" in value[0]) {
        logoArray.push(...value);
      } else {
        const checkedFiles = checkFileUpload({ files: value });
        const fileList = await getMediaObjects({
          fileList: checkedFiles,
          setPath: () =>
            `organizationsData/${formik.values.organizationId}/projects/${projectId}/infoPage`,
        });
        logoArray.push(...fileList);
      }
      setLogosState((prev) => {
        const newLogos = [...prev, ...logoArray].map(addUid);
        formik.setFieldValue(`content[${index}].images`, newLogos);
        return newLogos;
      });
    },
    [formik, index, projectId],
  );
  const setLogos = (value) => {
    setLogosState(() => {
      const newLogos = value.map(addUid);
      formik.setFieldValue(`content[${index}].images`, newLogos);
      return newLogos;
    });
  };

  const { openModal } = useModals();
  const { t } = useTranslation();
  const { invalidType, invalidSize, checkFileUpload } = useVerifyFiles();

  const fileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  const fileTypeLabel = {
    singular: "global:logo",
    plural: "global:logos",
  };
  return (
    <div className="relative flex flex-col gap-3">
      <div>
        <SortableLogos
          mode="edit"
          setLogos={setLogos}
          logos={logos}
          filePath={filePath}
          project={formik.values}
          saveToPath="infoPage"
        />
      </div>
      <div>
        <div className="z-10 flex items-center justify-between">
          <FileDrop
            onDrop={addLogos}
            fileTypes={fileTypes}
            icon={"add-image"}
            multiple
            onClick={() =>
              openModal(
                <ImagesUploadModal
                  maxItems={20}
                  fileTypes={fileTypes}
                  basePath={
                    projectId && organizationId
                      ? `organizationsData/${organizationId}/projects/${projectId}`
                      : ""
                  }
                  saveToPath="infoPage"
                  onInputChange={addLogos}
                  mediaBrowser
                />,
                {
                  title: t("util:add", {
                    val: t(getFileTypeLabel(20, fileTypes, fileTypeLabel)),
                  }),
                  size: "xl",
                  transform: false,
                  style: { height: "80vh" },
                },
              )
            }
          />
        </div>
      </div>

      <Toast
        id="saveSurveyMessage"
        leadingIcon={loading ? "loading" : "checkmark-lg"}
        leadingIconClassName={loading ? "" : "text-icon-success"}
        show={!!loading}
      >
        {loading
          ? `${t("util:isUploading", { val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)) })}...`
          : `${t("util:uploaded", { val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)) })}!`}
      </Toast>
      <Toast
        id="imageTooLarge"
        leadingIcon="alert-fill"
        leadingIconClassName="text-icon-critical"
        show={invalidSize}
      >
        {t("util:tooLarge", {
          val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)),
        })}
      </Toast>
      <Toast
        id="imagesOnly"
        leadingIcon="alert-fill"
        leadingIconClassName="text-icon-critical"
        show={invalidType}
      >
        {t("util:chooseAn", {
          val: t(getFileTypeLabel(1, fileTypes, fileTypeLabel)),
        })}
      </Toast>
    </div>
  );
};
const ImageSection = ({
  item,
  formik,
  index,
  defaultProps = {},
}: SectionProps<SectionImage> & {
  defaultProps?: { isVideo?: boolean };
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [loadingImage, setLoadingImage] = useState(false);
  const { organizationId, projectId } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: item?.images || [],
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  // Check both the defaultProps and existing item type for video status
  const isVideo = defaultProps.isVideo || item?.type === "video";

  const mimeTypes = isVideo ? ["video/"] : ["image/"];
  const maxSize = isVideo ? 500000000 : 50000000; // 500MB for video, 50MB for images

  const allowedFileTypes = isVideo
    ? ["video/mp4", "video/webm", "video/quicktime"]
    : ["image/jpg", "image/png", "image/jpeg", "image/webp"];

  const strippedFileTypes = allowedFileTypes
    .map((type) => `.${type.split("/").pop()}`)
    .join(", ");

  const { invalidType, invalidSize, checkFileUpload } = useVerifyFiles({
    mimeTypes,
    maxSize,
  });

  useEffect(() => {
    if (invalidType) {
      openModal(null, {
        title: t("infoPage:builder.invalidType"),
        description: t("infoPage:builder.invalidTypeDescription", {
          val: strippedFileTypes,
        }),
      });
      setLoadingImage(false);
    }
  }, [invalidType]);

  useEffect(() => {
    if (invalidSize) {
      openModal(null, {
        title: t("global:invalidSize"),
        description: t("infoPage:builder.invalidSizeDescription", {
          val: formatBytes(maxSize, { decimals: 0, si: true }),
        }),
      });
      setLoadingImage(false);
    }
  }, [invalidSize]);

  const handleFileDrop = async (files: ImageInput[]) => {
    setLoadingImage(true);

    try {
      // Handle new format (array of {image, alt} objects)
      if (Array.isArray(files) && files[0] && "url" in files[0]) {
        const imageObjects = files.map((obj) => ({
          url: (obj as { url: string }).url,
          alt: (obj as { alt: string }).alt,
        }));
        formik.setFieldValue(`content[${index}].images`, imageObjects);
        setLoadingImage(false);
        return;
      }

      // Handle old format (FileList or File[])
      const fileList = checkFileUpload({ files });

      const imageObjects = await getMediaObjects({
        fileList,
        setPath: () =>
          `organizationsData/${organizationId}/projects/${projectId}/`,
      });

      formik.setFieldValue(`content[${index}].images`, imageObjects);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingImage(false);
    }
  };
  return (
    <div className="grid gap-5 relative">
      {item?.images?.map(({ url, alt }, i) => {
        return (
          <div className="relative" key={i}>
            {!isVideo && (
              <div className="relative group/option max-w-max">
                <img
                  src={url}
                  alt={alt}
                  className={cn("rounded-xl")}
                  onLoad={() => setLoadingImage(false)}
                />
                <ModifyImageButtons
                  project={formik.values}
                  fileTypes={allowedFileTypes}
                  inputRef={fileInputRef}
                  imageUrl={url}
                  altText={alt}
                  altTextPlaceholder={imageMetadata[url]?.customMetadata?.alt}
                  copyright={
                    formik.values?.content?.[index]?.images?.[i]?.copyright
                  }
                  onAltTextChange={(value) => {
                    formik.setFieldValue(
                      `content[${index}].images[${i}].alt`,
                      value,
                    );
                  }}
                  onCopyrightChange={(value) => {
                    formik.setFieldValue(
                      `content[${index}].images[${i}].copyright`,
                      value,
                    );
                  }}
                  onChangeImage={handleFileDrop}
                  onRemoveImage={() => {
                    formik.setFieldValue(
                      `content[${index}].images`,
                      item?.images?.filter((_, i2) => i2 !== i),
                    );
                  }}
                  maxItems={1}
                  className="opacity-0 group-hover/option:opacity-100"
                  saveToPath="infoPage"
                />
              </div>
            )}
            {isVideo && (
              <div className="relative group/option">
                {/* biome-ignore lint/a11y/useMediaCaption: <explanation> */}
                <video
                  className={cn("mx-auto rounded-xl bg-black w-full")}
                  controls
                  playsInline
                  webkit-playsinline="true"
                  x5-playsinline="true"
                  preload="auto"
                  onLoadedData={() => setLoadingImage(false)}
                >
                  <source src={url} type="video/mp4" />
                  <source src={url} type="video/quicktime" />
                  Your browser does not support the video tag.
                </video>
                <ModifyImageButtons
                  type="video"
                  fileTypes={allowedFileTypes}
                  project={formik.values}
                  inputRef={fileInputRef}
                  imageUrl={url}
                  altText={alt}
                  altTextPlaceholder={imageMetadata[url]?.customMetadata?.alt}
                  copyright={
                    formik.values?.content?.[index]?.images?.[i]?.copyright
                  }
                  onAltTextChange={(value) => {
                    formik.setFieldValue(
                      `content[${index}].images[${i}].alt`,
                      value,
                    );
                  }}
                  onCopyrightChange={(value) => {
                    formik.setFieldValue(
                      `content[${index}].images[${i}].copyright`,
                      value,
                    );
                  }}
                  onChangeImage={handleFileDrop}
                  onRemoveImage={() => {
                    formik.setFieldValue(
                      `content[${index}].images`,
                      item?.images?.filter((_, i2) => i2 !== i),
                    );
                  }}
                  maxItems={1}
                  saveToPath="infoPage"
                />
              </div>
            )}
          </div>
        );
      })}

      <FileDrop
        onDrop={handleFileDrop}
        ref={fileInputRef}
        fileTypes={allowedFileTypes}
        icon={loadingImage ? "loading" : isVideo ? "add-video" : "add-image"}
        className={
          item?.images?.[0]?.url ? "pointer-events-none absolute opacity-0" : ""
        }
        onClick={() =>
          openModal(
            <ImagesUploadModal
              onInputChange={handleFileDrop}
              maxItems={1}
              fileTypes={allowedFileTypes}
              stock={!isVideo}
              ai={!isVideo}
              mediaBrowser
              basePath={
                projectId && organizationId
                  ? `organizationsData/${organizationId}/projects/${projectId}`
                  : ""
              }
              saveToPath="infoPage"
              project={formik.values}
            />,
            {
              title: t("util:add", {
                val: t(getFileTypeLabel(1, allowedFileTypes)),
              }),
              size: "xl",
              transform: false,
              style: { height: "80vh" },
            },
          )
        }
      />
    </div>
  );
};
const FileSection = ({ item, formik, index }: SectionProps<SectionFile>) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [files, setFilesState] = useState<ImageInput[]>(item?.files || []);

  const { projectId, organizationId } = useParams();
  const maxSize = 50000000;
  const allowedFileTypes = ["application/pdf"];
  const strippedFileTypes = allowedFileTypes
    .map((type) => {
      return `.${type.split("/").pop()}`;
    })
    .join(", ");

  const { invalidType, invalidSize, checkFileUpload } = useVerifyFiles({
    mimeTypes: allowedFileTypes,
    maxSize,
  });
  useEffect(() => {
    if (invalidType) {
      setLoadingFiles(false);
      openModal(null, {
        title: t("infoPage:builder.invalidType"),
        description: t("infoPage:builder.invalidTypeDescription", {
          val: strippedFileTypes,
        }),
      });
    }
  }, [invalidType]);
  useEffect(() => {
    if (invalidSize) {
      setLoadingFiles(false);
      openModal(null, {
        title: t("global:invalidSize"),
        description: t("infoPage:builder.invalidSizeDescription", {
          val: formatBytes(maxSize, { decimals: 0, si: true }),
        }),
      });
    }
  }, [invalidSize]);

  const addFiles = useCallback(
    async (files: ImageInput) => {
      setLoadingFiles(true);
      let fileArray: ImageInput = [];
      if (Array.isArray(files) && files[0] && "url" in files[0]) {
        fileArray = files;
      } else {
        fileArray = await getMediaObjects({
          fileList: checkFileUpload({ files }),
          setPath: () =>
            `organizationsData/${formik.values.organizationId}/projects/${projectId}/infoPage`,
        });
      }
      setFilesState((prev) => {
        const newFiles = [...prev, ...fileArray];
        formik.setFieldValue(`content[${index}].files`, newFiles);
        return newFiles;
      });
      setLoadingFiles(false);
    },
    [formik, index, projectId, setFilesState],
  );
  const handleDeleteFile = (i: number) => {
    setFilesState((prev) => {
      const newFiles = prev.filter((_, k) => k !== i);
      formik.setFieldValue(`content[${index}].files`, newFiles);
      return newFiles;
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {!!files?.length && (
        <div className="border-border-default flex flex-col rounded-2xl border p-1">
          {files.map((file, i) => {
            return (
              <ListItem
                key={file.url}
                index={i}
                primaryContent={<TruncateMiddle text={file.name} />}
                secondaryContent={formatBytes(file.size, { decimals: 1 })}
                // selected={activeQuestion === pages.length + 1}
                leadingIcon="file/pdf-28"
                onClick={(e) => {
                  e.preventDefault();
                  window.open(file?.blobUrl || file?.url, "_blank");
                }}
                action={
                  <Button
                    leadingIcon="trash"
                    classNameWrapper="flex items-center"
                    critical
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleDeleteFile(i);
                    }}
                  />
                }
              />
            );
          })}
        </div>
      )}
      <FileDrop
        // text={t("util:add", { val: t("global:PDF") })}
        loading={loadingFiles}
        onDrop={addFiles}
        className="flex flex-col gap-5"
        fileTypes={allowedFileTypes}
        multiple
        onClick={() =>
          openModal(
            <ImagesUploadModal
              maxItems={20}
              fileTypes={allowedFileTypes}
              mediaBrowser
              basePath={
                projectId && organizationId
                  ? `organizationsData/${organizationId}/projects/${projectId}`
                  : ""
              }
              saveToPath="infoPage"
              onInputChange={addFiles}
            />,
            {
              title: t("util:add", {
                val: t(getFileTypeLabel(20, allowedFileTypes)),
              }),
              size: "xl",
              transform: false,
              style: { height: "80vh" },
            },
          )
        }
      />
    </div>
  );
};
const ButtonSection = memo(
  ({ item, formik, index }: SectionProps<SectionButton>) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(formik);

    useEffect(() => {
      if (!item.buttons?.[0]?.uid) {
        setFieldValue(`content[${index}].buttons[${0}].uid`, uuidv4());
      }
    }, [index, item.buttons, setFieldValue]);

    return (
      <div className="flex flex-col gap-5">
        <div>
          <InfoToast
            text={t("infoPage:builder.buttonSection.infoToast")}
            className="max-w-full text-sm leading-tight"
            transition={false}
          />
        </div>
        <div className="flex items-center gap-2">
          <OptimizedInput
            fieldPath={`content[${index}].buttons[${0}].link`}
            value={item?.buttons?.[0]?.link || ""}
            onFieldChange={setFieldValue}
            leadingIcon={
              item?.buttons?.[0]?.link?.includes("@") ? "mail" : "link"
            }
            placeholder="Link / E-Mail"
            maxRows={1}
          />
          <OptimizedInput
            fieldPath={`content[${index}].buttons[${0}].text`}
            value={item?.buttons?.[0]?.text || ""}
            leadingIcon="edit"
            onFieldChange={setFieldValue}
            placeholder={`Button Text (${t("global:optional").toLowerCase()})`}
            maxRows={1}
          />
        </div>
      </div>
    );
  },
);
export const EventThumbnail = ({ event }) => {
  const { i18n } = useTranslation();
  moment.locale(i18n.resolvedLanguage);
  const start = moment(event?.start.date);
  const day = start.format("DD");
  const weekday = start.format("ddd");
  const pastEvent = moment(event.end.date).isBefore(moment());

  return (
    <div
      className={cn(
        "bg-background-default group-hover:surface-pressed border-border-subdued flex h-20 w-20 shrink-0 flex-col items-center justify-center rounded-lg group-hover:border",
      )}
    >
      <span
        className={cn(
          "body-md-semibold",
          pastEvent ? "text-text-subdued" : "text-text-critical",
        )}
      >
        {capitalizeFirstLetter(weekday)}
      </span>
      <span className={cn("heading-3xl", pastEvent ? "text-text-subdued" : "")}>
        {day}
      </span>
    </div>
  );
};
export const EventItem = ({
  event,
  onDelete,
  onEdit,
  onDuplicate,
  className,
  isActive,
  ...props
}: {
  event: SectionCalendar["events"][number];
  className?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onDelete?: (uid: string) => void;
  onEdit?: (event: SectionCalendar["events"][number]) => void;
  onDuplicate?: (event: SectionCalendar["events"][number]) => void;
  isActive?: (event: SectionCalendar["events"][number]) => boolean;
}) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  return (
    <div className=" -mx-3">
      <button
        {...props}
        className={cn(
          "hover:bg-background-default focusable group relative flex w-full cursor-pointer items-center gap-5 rounded-xl p-3",
          isActive?.(event) && "bg-surface-hovered hover:bg-surface-pressed",
          className,
        )}
        onMouseDown={(e) => {
          props.onClick?.(e);
          onEdit?.(event);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            props.onClick?.(e);
            onEdit?.(event);
          }
        }}
      >
        <EventThumbnail event={event} />
        <div className="flex grow flex-col items-start gap-2">
          {event?.iCalUID?.includes("google") && (
            <Icon
              name="logo/Google"
              className="[&_*]:fill-icon-subdued absolute right-3 top-3 h-4 w-4"
            />
          )}
          {/* {pastEvent && (
          <span className="text-text-subdued bg-background-hovered rounded p-2 py-1 text-xs font-semibold leading-none">
            vergangen
          </span>
        )} */}
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "heading-xl text-left leading-none",
                !event?.summary && "text-text-subdued",
              )}
            >
              {translation?.[event?.summary] ||
                event?.summary ||
                t("util:new", { val: t("global:event") })}
            </span>
          </div>
          <span
            className={cn(
              "body-md line-clamp-2 whitespace-normal text-left leading-tight",
              !event?.description && "text-text-subdued",
            )}
          >
            {translation?.[event?.description] || event?.description || ""}
          </span>
        </div>
        {((onEdit && event) || (onDelete && event?.uid)) && (
          <div className="mr-2 flex flex-col gap-2 opacity-0 group-hover:opacity-100">
            <Dropdown
              size="sm"
              variant="shapeless"
              trailingIcon="more"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              closeOnClick={true}
              items={[
                {
                  text: t("global:edit"),
                  leadingIcon: "edit",
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();

                    onEdit(event);
                  },
                },
                {
                  text: t("global:duplicate"),
                  leadingIcon: "duplicate",
                  onClick: (e) => {
                    onDuplicate(event);
                  },
                },
                {
                  text: t("global:delete"),
                  leadingIcon: "trash",
                  critical: true,
                  onClick: (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onDelete(event.uid);
                  },
                },
              ]}
            />
          </div>
        )}
      </button>
    </div>
  );
};

interface TimeInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  maxLength?: number;
  pattern?: string;
  minTime?: string;
  maxTime?: string;
  date?: string;
}

const splitTime = (timeString: string) => {
  const [h = "00", m = "00"] = timeString
    .replace(/\D/g, "")
    .padStart(2, "0")
    .match(/\d{2}/g) || ["00", "00"];
  return [h, m] as const;
};
const joinTime = (time: [string, string]) => {
  return Number.parseInt(time?.join(""), 10);
};

const isInRange = ({ value, min, max }) => {
  return (
    (min && joinTime(value) > joinTime([...splitTime(min)])) ||
    (max && joinTime(value) < joinTime([...splitTime(max)]))
  );
};

const TimeInput = ({
  value,
  date,
  onChange,
  minTime,
  maxTime,
  placeholder = "HH:MM",
  maxLength = 5,
  pattern = "[0-9:.]*",
}: TimeInputProps) => {
  const ref = useRef<HTMLLabelElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const id = uuidv4();
  const [intermediateValue, setIntermediateValue] = useState(value);
  const debounceRef = useRef<NodeJS.Timeout>();
  useEffect(() => setIntermediateValue(value), [value]);
  const [isFocused, setIsFocused] = useState(false);
  function formatTime(timeString: string) {
    let [h, m] = splitTime(timeString);
    if (maxTime && joinTime([h, m]) > joinTime([...splitTime(maxTime)])) {
      [h, m] = splitTime(maxTime);
    }
    if (minTime && joinTime([h, m]) < joinTime([...splitTime(minTime)])) {
      [h, m] = splitTime(minTime);
    }
    const formattedTime = moment(date).hour(h).minute(m).format("HH:mm");
    setIntermediateValue(formattedTime);
    onChange(formattedTime);
  }
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const isValid = new RegExp(pattern).test(inputValue);

    if (isValid) {
      setIntermediateValue(inputValue);

      // Debounce the formatting to avoid too many moment.js operations
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        if (inputValue.length >= 3) {
          try {
            formatTime(inputValue);
          } catch (error) {
            console.warn("Time formatting error:", error);
          }
        }
      }, 200); // Slightly longer debounce for time input to reduce moment.js calls
    }
  };

  return (
    <div ref={containerRef} className="w-full">
      <Dropdown
        items={[...Array(24).keys()].map((h) => {
          const time = `${h.toString().padStart(2, "0")}:00`;
          return {
            text: time,
            active: time === value,
            onClick: () => {
              setIntermediateValue(time);
              onChange(time);
            },
            disabled: !isInRange({
              value: [...splitTime(time)],
              min: minTime,
              max: maxTime,
            }),
          };
        })}
        full
        hasArrow={false}
        trailingIcon={false}
        Button={({ children }) => <div>{children}</div>}
        modal={false}
        onOpenChange={setIsFocused}
        classNames={{
          trigger: "w-full",
          content: "!max-h-[200px] rounded-lg",
          dropdownItem: "flex !min-w-0 justify-center !rounded text-center",
        }}
        portalContainer={containerRef.current}
        trigger={
          <label
            className="relative flex w-full items-center gap-2"
            htmlFor={id}
            ref={ref}
          >
            <Icon name="clock" className="absolute left-3 shrink-0" />
            <input
              type="text"
              id={id}
              value={intermediateValue}
              onChange={handleInputChange}
              onBlur={() => {
                clearTimeout(debounceRef.current);
                formatTime(intermediateValue);
              }}
              // onFocus={() => {
              //   setIsFocused(true);
              // }}
              placeholder={placeholder}
              maxLength={maxLength}
              pattern={pattern}
              className={cn(
                "bg-background-default border-border-subdued focus-visible:outline-border-highlight-default h-11 w-full rounded-xl border p-3 pl-9",
                isFocused
                  ? "outline-border-highlight-default outline outline-2 -outline-offset-2"
                  : "",
              )}
              size={1}
            />
          </label>
        }
      />
    </div>
  );
};
const DateTimePicker = <
  T extends {
    start: CalendarEvent["start"];
    end: CalendarEvent["end"];
  },
>({
  value,
  onChange,
  events,
}: {
  value: T;
  onChange: (e: T) => void;
  events: SectionCalendar["events"];
}) => {
  const { t } = useTranslation();
  const start = moment(value?.start?.date);
  const end = moment(value?.end?.date);
  const handleDateChange = (date: moment.Moment | null) => {
    if (!date) return;
    onChange({
      ...value,
      start: {
        date: date?.hour(start.hour()).minute(start.minute()).toISOString(),
      },
      end: {
        date: date?.hour(end.hour()).minute(end.minute()).toISOString(),
      },
    });
  };
  const handleTimeChange = (timeString: string, key: "start" | "end") => {
    if (!timeString) return;
    const [h, m] = timeString.split(":").map(Number);
    const date: Moment = moment(value?.[key].date)?.hour(h).minute(m);

    const updatedValue: typeof value = {
      ...value,
      [key]: {
        date: date.toISOString(),
      },
    };
    if (key === "start" && date.isAfter(moment(value?.end?.date))) {
      updatedValue.end = {
        date: date.toISOString(),
      };
    }
    onChange(updatedValue);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="-m-5">
        <DayPickerSingleDateController
          date={moment(value?.start?.date)}
          onDateChange={(date) => handleDateChange(date)}
          isDayHighlighted={(e) =>
            events?.some(
              (date) =>
                moment(date.start?.date).format("DD-MM-YYYY") ===
                  e.format("DD-MM-YYYY") &&
                start.format("DD-MM-YYYY") !== e.format("DD-MM-YYYY"),
            )
          }
          focused
          onFocusChange={() => {}}
          initialVisibleMonth={() => moment()}
          noBorder
          hideKeyboardShortcutsPanel
          numberOfMonths={1}
          daySize={36}
          navPrev={<Button variant="shapeless" leadingIcon="arrow-left-md" />}
          navNext={<Button variant="shapeless" leadingIcon="arrow-right-md" />}
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="heading-md">{t("global:clockTime")}</span>
        <div className="mx-auto flex w-full items-center justify-center gap-2">
          <TimeInput
            label="Start Time"
            value={start.format("HH:mm")}
            onChange={(value) => handleTimeChange(value, "start")}
            placeholder="HH:MM"
            maxTime={"24:00"}
            date={value?.start?.date}
          />
          {" – "}
          <TimeInput
            label="End Time"
            value={end.format("HH:mm")}
            onChange={(value) => handleTimeChange(value, "end")}
            placeholder="HH:MM"
            minTime={start.format("HH:mm")}
            date={value?.end?.date}
          />
        </div>
      </div>
    </div>
  );
};
interface EventDetailsProps {
  event: CalendarEvent;
  events: CalendarEvent[];
  index: number;
  formik: FormikProps<Project>;
  handleClose: (event?: MouseEvent) => void;
  handleDelete: (uid: string) => void;
  handleDuplicate: (event: CalendarEvent) => void;
}
const EventDetails = memo(
  ({
    event,
    index,
    events,
    formik,
    handleClose,
    handleDelete,
    handleDuplicate,
  }: EventDetailsProps) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(formik);
    const i = events?.findIndex((e) => e.uid === event?.uid);
    const ref = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const geocoderDropdownRef = useRef<HTMLDivElement>(null);

    useOnClickOutside(ref, handleClose, {
      ignore: [dropdownRef, geocoderDropdownRef],
    });

    return (
      <Transition
        show={!!event}
        style={{
          zIndex: 99999,
          right: 0,
          top: 76,
          height: "calc(100vh - 76px)",
          position: "fixed",
        }}
        from={{ opacity: 0, scale: 1, x: 200 }}
        leave={{ opacity: 0, scale: 1, x: 300 }}
        enter={{ opacity: 1, scale: 1, x: 0 }}
      >
        <div
          className="bg-surface-default border-border-subdued -mr-16 flex h-full flex-col gap-5 overflow-y-auto border-l p-5 pr-20"
          ref={ref}
        >
          <div className="flex items-center justify-between">
            <span className="heading-xl">
              {t("util:edit", { val: t("global:event") })}
            </span>
            <div className="flex gap-2">
              <Dropdown
                size="sm"
                variant="shapeless"
                trailingIcon="more"
                closeOnClick={true}
                contentRef={dropdownRef}
                items={[
                  {
                    text: t("global:duplicate"),
                    leadingIcon: "duplicate",
                    onClick: () => {
                      handleClose();
                      handleDuplicate(event);
                    },
                  },
                  {
                    text: t("global:delete"),
                    leadingIcon: "trash",
                    critical: true,
                    onClick: () => {
                      handleClose();
                      handleDelete(event?.uid);
                    },
                  },
                ]}
              />
              <Button onClick={handleClose} leadingIcon="arrowhead-right-md" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-5">
            <div className="flex flex-col gap-2">
              <OptimizedInput
                fieldPath={`content[${index}].events[${i}].summary`}
                value={event?.summary || ""}
                onFieldChange={setFieldValue}
                label={t("global:title")}
              />
              <OptimizedInput
                fieldPath={`content[${index}].events[${i}].description`}
                value={event?.description || ""}
                onFieldChange={setFieldValue}
                rows={3}
                type="textarea"
                label={t("global:description")}
              />
              <OptimizedInput
                fieldPath={`content[${index}].events[${i}].url`}
                value={event?.url || ""}
                onFieldChange={setFieldValue}
                rows={1}
                type="url"
                label="URL"
              />
              {event?.url && (
                <OptimizedInput
                  fieldPath={`content[${index}].events[${i}].xProps.urlLabel`}
                  value={event?.xProps?.urlLabel || ""}
                  onFieldChange={setFieldValue}
                  rows={1}
                  label={t("global:URLLabel")}
                />
              )}
              <OptimizedInput
                key={event?.uid}
                fieldPath={`content[${index}].events[${i}].location`}
                value={event?.location || ""}
                onFieldChange={setFieldValue}
                label={t("global:place")}
                placeholder={t("util:search", { val: t("global:place") })}
              />
            </div>

            <DateTimePicker
              value={event}
              key={event?.uid}
              events={events}
              onChange={(newEvent) =>
                formik.setFieldValue(
                  `content[${index}].events`,
                  events
                    .map((e) => {
                      if (e.uid === newEvent.uid) {
                        return newEvent;
                      }
                      return e;
                    })
                    ?.sort(
                      (a, b) =>
                        new Date(a.start.date).getTime() -
                        new Date(b.start.date).getTime(),
                    ),
                )
              }
              // `[${i}]`, {
              //   ...event,
              //   ...e,
            />
          </div>
        </div>
      </Transition>
    );
  },
);

const EventItemWrapper = ({ item, global, index }) => {
  // Add null checks to prevent errors
  if (!item || !item.start || !item.start.date) {
    return null;
  }

  return (
    <div>
      {((global.events[index - 1] &&
        global.events[index - 1].start?.date &&
        moment(global.events[index - 1].start.date).format("YYYY-MM") !==
          moment(item.start.date).format("YYYY-MM")) ||
        index === 0) && (
        <span className="heading-lg text-text-subdued mt-5">
          {moment(item.start.date).format("MMMM YYYY")}
        </span>
      )}
      <EventItem
        key={item.uid}
        event={item}
        onDelete={global.onDelete}
        onEdit={global.onEdit}
        onDuplicate={global.onDuplicate}
        isActive={global.isActive}
      />
    </div>
  );
};

const CalendarSection = memo(
  ({ item, formik, index }: SectionProps<SectionCalendar>) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(formik);
    const [activeEvent, setActiveEvent] = useState<CalendarEvent>();
    const timeoutRef = useRef<NodeJS.Timeout>();
    const events = item?.events || [];

    const handleAdd = useCallback(() => {
      const newEvent = {
        uid: uuidv4(),
        summary: "",
        description: "",
        start: {
          date: moment()
            .hour(moment().hour() + 1)
            .minute(0)
            .toISOString(),
        },
        end: {
          date: moment()
            .hour(moment().hour() + 2)
            .minute(0)
            .toISOString(),
        },
      };
      setFieldValue(`content[${index}].events`, [
        ...events,
        newEvent,
      ] satisfies CalendarEvent[]);
      setActiveEvent(newEvent);
    }, [events, index, setFieldValue]);

    const handleDuplicate = useCallback(
      (eventToDuplicate: CalendarEvent) => {
        const duplicatedEvent = {
          ...eventToDuplicate,
          uid: uuidv4(), // Generate a new unique ID for the duplicated event
          summary: `${eventToDuplicate.summary} (${t("util:copied").trim()})`, // Optionally indicate that this is a copy
        };

        // Find the index of the event to duplicate
        const indexToInsert = events.findIndex(
          (event) => event.uid === eventToDuplicate.uid,
        );

        // Insert the duplicated event immediately after the original
        const updatedEvents = [
          ...events.slice(0, indexToInsert + 1),
          duplicatedEvent,
          ...events.slice(indexToInsert + 1),
        ];

        // Update the events array in the form state
        setFieldValue(`content[${index}].events`, updatedEvents);

        // Optionally set the duplicated event as the active event
        setTimeout(() => {
          setActiveEvent(duplicatedEvent);
        }, 100);
      },
      [events, index, setFieldValue, t],
    );

    const handleDelete = useCallback(
      (uid: string) => {
        setFieldValue(
          `content[${index}].events`,
          events.filter((e) => e.uid !== uid),
        );
      },
      [events, index, setFieldValue],
    );

    return (
      <div className="flex flex-col gap-10">
        <TextSection
          item={{
            ...item,
            type: "text",
          }}
          defaultTitle={t("global:calendar")}
          formik={formik}
          index={index}
          withBody={false}
          rows={1}
        />
        <EventDetails
          event={(
            formik.values?.content?.[index] as SectionCalendar
          )?.events?.find((e) => e.uid === activeEvent?.uid)}
          formik={formik}
          events={item.events}
          index={index}
          handleClose={() => setActiveEvent(undefined)}
          handleDelete={handleDelete}
          handleDuplicate={handleDuplicate}
        />
        <div className="flex flex-col gap-5">
          <SortableSections
            className="grid gap-2"
            axis="y"
            items={events}
            Wrapper={SortableWrapper}
            Component={EventItemWrapper}
            global={{
              formik,
              events,
              onDelete: handleDelete,
              onDuplicate: handleDuplicate,
              onEdit: (event: EventDetailsProps["event"]) => {
                clearTimeout(timeoutRef.current);
                setActiveEvent((e) => (e ? undefined : event));
                timeoutRef.current = setTimeout(
                  () =>
                    event?.uid !== activeEvent?.uid && setActiveEvent(event),
                  100,
                );
              },
              isActive: (event: EventDetailsProps["event"]) =>
                event?.uid === activeEvent?.uid,
            }}
          />
          <Button
            leadingIcon="plus-md"
            classNameWrapper="w-full"
            className="w-full"
            size="lg"
            variant="outline"
            // pillShaped={false}
            onClick={handleAdd}
            text={t("util:add", { val: t("global:event") })}
          />
        </div>
        {/* <GoogleCalendarIdInput formik={formik} /> */}
      </div>
    );
  },
);
interface FAQBuilderProps {
  item: SectionFAQ["faq"][number];
  global: {
    formik: FormikProps<Project>;
    sectionIndex: number;
  };
  index: number;
  listeners: SyntheticListenerMap;
}
const FAQBuilder = memo(
  ({ item, global, index, listeners }: FAQBuilderProps) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(global.formik);

    const handleDelete = useCallback(() => {
      const currentFaq =
        (global?.formik.values?.content?.[global?.sectionIndex] as any)?.faq ||
        [];
      setFieldValue(
        `content[${global?.sectionIndex}].faq`,
        currentFaq.filter((_: any, i: number) => i !== index),
      );
    }, [
      global?.formik.values?.content,
      global?.sectionIndex,
      index,
      setFieldValue,
    ]);

    return (
      <div className="group/faq-item flex flex-col">
        <div className="flex items-center gap-5">
          <OptimizedInvisibleInput
            fieldPath={`content[${global?.sectionIndex}].faq[${index}].question`}
            value={item.question || ""}
            onFieldChange={setFieldValue}
            placeholder={`${t("global:question")} ${index + 1}`}
            className="body-md-bold"
          />
          <div className="ml-auto flex items-center gap-2">
            <Button
              leadingIcon="trash"
              critical
              className="opacity-0 group-hover/faq-item:opacity-100"
              variant="shapeless"
              onClick={handleDelete}
              tooltipLabel={t("util:delete", { val: t("global:question") })}
            />
            <div
              {...listeners}
              className="text-text-subdued hover:text-text-default flex cursor-grab items-center active:cursor-grabbing"
            >
              <Button
                leadingIcon="handle-vertical"
                variant="shapeless"
                className=" cursor-grab hover:bg-transparent active:cursor-grabbing active:bg-transparent"
                tooltipLabel={t("util:move", { val: t("global:question") })}
              />
            </div>
          </div>
        </div>
        <OptimizedInvisibleInput
          fieldPath={`content[${global?.sectionIndex}].faq[${index}].answer`}
          value={item.answer || ""}
          id={item?.uid}
          onFieldChange={setFieldValue}
          placeholder={t("global:answer")}
          maxRows={100}
          rich
        />
      </div>
    );
  },
);
const FAQSection = ({ item, formik, index }: SectionProps<SectionFAQ>) => {
  const { t } = useTranslation();
  const faq = item.faq || [];
  return (
    <div className="flex flex-col gap-5">
      <div>
        <InfoToast
          text={t("infoPage:builder.faqSection.infoToast")}
          className="max-w-full text-sm leading-tight"
          transition={false}
        />
      </div>
      <div>
        <TextSection
          item={{
            ...item,
            type: "text",
          }}
          defaultTitle={t("infoPage:faq.title")}
          defaultBody={t("infoPage:faq.description")}
          formik={formik}
          index={index}
          rows={1}
        />
      </div>
      <SortableSections
        className="grid gap-2"
        axis="y"
        items={faq || []}
        setItems={(items) =>
          formik.setFieldValue(`content[${index}].faq`, items)
        }
        Wrapper={SortableWrapper}
        Component={FAQBuilder}
        global={{ formik, sectionIndex: index }}
      />
      <Button
        text={t("util:add", { val: t("global:question") })}
        leadingIcon="plus-md"
        onClick={() =>
          formik.setFieldValue(`content[${index}].faq`, [
            ...(faq || []),
            { uid: uuidv4(), title: "", body: "" },
          ])
        }
      />
    </div>
  );
};

interface TimelineBuilderProps {
  item: SectionTimeline["timeline"][number];
  global: {
    formik: FormikProps<Project>;
    sectionIndex: number;
  };
  index: number;
  listeners: SyntheticListenerMap;
}

const TimelineBuilder = memo(
  ({ item, global, index, listeners }: TimelineBuilderProps) => {
    const { t } = useTranslation();
    const { setFieldValue } = useOptimizedFormik(global.formik);

    const handleDelete = useCallback(() => {
      const currentTimeline =
        (global?.formik.values?.content?.[global?.sectionIndex] as any)
          ?.timeline || [];
      setFieldValue(
        `content[${global?.sectionIndex}].timeline`,
        currentTimeline.filter((_: any, i: number) => i !== index),
      );
    }, [
      global?.formik.values?.content,
      global?.sectionIndex,
      index,
      setFieldValue,
    ]);

    return (
      <div className="group/timeline-item flex flex-col">
        <div className="flex items-center gap-5">
          <OptimizedInvisibleInput
            fieldPath={`content[${global?.sectionIndex}].timeline[${index}].title`}
            value={item.title || ""}
            onFieldChange={setFieldValue}
            placeholder={t("global:title")}
            className="heading-xl"
          />
          <div className="ml-auto flex items-center gap-2">
            <Button
              leadingIcon="trash"
              critical
              className="opacity-0 group-hover/timeline-item:opacity-100"
              variant="shapeless"
              onClick={handleDelete}
              tooltipLabel={t("util:delete", { val: t("global:entry") })}
            />
            <div
              {...listeners}
              className="text-text-subdued hover:text-text-default flex cursor-grab items-center active:cursor-grabbing"
            >
              <Button
                leadingIcon="handle-vertical"
                variant="shapeless"
                className="cursor-grab hover:bg-transparent active:cursor-grabbing active:bg-transparent"
                tooltipLabel={t("util:move", { val: t("global:entry") })}
              />
            </div>
          </div>
        </div>
        <OptimizedInvisibleInput
          fieldPath={`content[${global?.sectionIndex}].timeline[${index}].description`}
          value={item.description || ""}
          id={item?.uid}
          onFieldChange={setFieldValue}
          placeholder={t("global:description")}
          maxRows={100}
          rich
        />
      </div>
    );
  },
);

const TimelineSection = ({
  item,
  formik,
  index,
}: SectionProps<SectionTimeline>) => {
  const { t } = useTranslation();
  const timeline = item.timeline || [];
  return (
    <div className="flex flex-col gap-5">
      <div>
        <InfoToast
          text={t("infoPage:builder.timelineSection.infoToast")}
          className="max-w-full text-sm leading-tight"
          transition={false}
        />
      </div>
      <div>
        <TextSection
          item={{
            ...item,
            type: "text",
          }}
          defaultTitle={t("global:timeline")}
          defaultBody={t("infoPage:timeline.description")}
          formik={formik}
          index={index}
          rows={1}
        />
      </div>
      <SortableSections
        className="grid gap-2"
        axis="y"
        items={timeline || []}
        setItems={(items) =>
          formik.setFieldValue(`content[${index}].timeline`, items)
        }
        Wrapper={SortableWrapper}
        Component={TimelineBuilder}
        global={{ formik, sectionIndex: index }}
      />
      <Button
        text={t("util:add", { val: t("global:entry") })}
        leadingIcon="plus-md"
        onClick={() =>
          formik.setFieldValue(`content[${index}].timeline`, [
            ...(timeline || []),
            { uid: uuidv4(), date: "", title: "", description: "" },
          ])
        }
      />
    </div>
  );
};

export const useSectionTypes = ({
  formik,
  insertIndex,
}: {
  formik: FormikProps<Project>;
  insertIndex?: number;
}) => {
  const { t } = useTranslation();

  const onClick = (type: string) => {
    console.log("adding", type);
    const newSection = { uid: uuidv4(), type };
    const currentContent = formik.values?.content || [];

    if (typeof insertIndex === "number") {
      const newContent = [...currentContent];
      newContent.splice(insertIndex, 0, newSection);
      formik.setFieldValue("content", newContent);
      // Scroll to the new section after a short delay to allow for DOM update
      setTimeout(() => {
        document.getElementById(newSection.uid)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    } else {
      formik.setFieldValue("content", [...currentContent, newSection]);
      // Scroll to the new section after a short delay to allow for DOM update
      setTimeout(() => {
        document.getElementById(newSection.uid)?.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      });
    }
  };

  return {
    text: {
      title: t("global:textSection"),
      icon: "paragraph",
      component: TextSection,
      onClick: () => onClick("text"),
    } as const,
    image: {
      title: t("global:image"),
      icon: "image",
      component: ImageSection,
      onClick: () => onClick("image"),
    } as const,
    logo: {
      title: t("global:logo"),
      icon: "room",
      component: LogoSection,
      onClick: () => onClick("logo"),
    } as const,
    map: {
      title: t("global:map"),
      icon: "map-alt",
      component: MapSection,
      onClick: () => onClick("map"),
    } as const,
    video: {
      title: t("global:video"),
      icon: "video",
      component: ImageSection,
      onClick: () => onClick("video"),
      defaultProps: {
        isVideo: true,
      },
    } as const,
    file: {
      title: "PDF",
      icon: "document",
      component: FileSection,
      onClick: () => onClick("file"),
    } as const,
    button: {
      title: "Button",
      icon: "link",
      onClick: () => onClick("button"),
      component: ButtonSection,
    } as const,
    calendar: {
      title: t("global:calendar"),
      icon: "calendar",
      component: CalendarSection,
      onClick: () => onClick("calendar"),
    } as const,
    faq: {
      title: "FAQ",
      icon: "bulb-alt",
      component: FAQSection,
      onClick: () => onClick("faq"),
    } as const,
    timeline: {
      title: t("global:timeline"),
      icon: "timeline",
      component: TimelineSection,
      onClick: () => onClick("timeline"),
    } as const,
  };
};

export const Section = memo(
  ({
    item,
    global,
    index,
    listeners,
  }: {
    item: NonNullable<Project["content"]>[number];
    global: {
      [key: string]: any;
      formik: FormikProps<Project>;
      setCollapsed: React.Dispatch<React.SetStateAction<string[]>>;
      collapsed: string[];
      project: Project;
      mapEditorActive: string | null;
      setMapEditorActive: React.Dispatch<React.SetStateAction<string | null>>;
    };
    index: number;
    listeners: SyntheticListenerMap;
  }) => {
    const { displayDiscardModal } = useDiscardModal();
    const collapsed = global?.collapsed?.includes(item.uid);
    const { t } = useTranslation();
    const { toSentenceCase } = useConvertCase();
    const { formik, project, setProject, mapEditorActive, setMapEditorActive } =
      global;
    const type = item?.type || (item.files?.urls?.length ? "file" : "text");
    const { title, component: Component } =
      useSectionTypes({ formik })[type || "text"] || {};

    if (!item || !Component) return null;
    return (
      <div
        id={item.uid}
        className={cn(
          "mx-auto flex max-w-2xl flex-col gap-2",
          collapsed ? "h-14" : "p-14",
        )}
      >
        <div className="absolute left-3 right-3 top-3 flex items-center gap-2">
          <div
            {...listeners}
            className="text-text-subdued hover:text-text-default flex cursor-grab items-center active:cursor-grabbing"
          >
            <Button
              leadingIcon="handle-vertical"
              variant="shapeless"
              className="cursor-grab hover:bg-transparent active:cursor-grabbing active:bg-transparent"
              tooltipLabel={t("util:move", { val: t("global:section") })}
            />
            <span className="body-md-bold">{toSentenceCase(title)}</span>{" "}
            {item?.title && collapsed && (
              <>
                <span className="heading-2xl text-text-subdued ml-3"> · </span>
                <span className="body-md-bold ml-3 text-black">
                  {item.title}
                </span>
              </>
            )}
          </div>
          <Button
            leadingIcon="trash"
            critical
            className="opacity-0 group-hover/section:opacity-100"
            variant="shapeless"
            onClick={() =>
              displayDiscardModal({
                title: t("util:delete", { val: t("global:section") }),
                description: "",
                submitText: t("global:delete"),
                onSubmit: () =>
                  formik.setFieldValue(
                    "content",
                    formik.values?.content?.filter((_, i) => i !== index),
                  ),
              })
            }
            tooltipLabel={t("util:delete", { val: t("global:section") })}
            classNameWrapper="ml-auto"
          />
          <Button
            trailingIcon={
              collapsed ? "arrowhead-extend-lg" : "arrowhead-collapse-lg"
            }
            className={cn(
              "opacity-0 group-hover/section:opacity-100",
              collapsed && "opacity-100",
            )}
            variant="shapeless"
            onClick={() =>
              global?.setCollapsed?.((e) =>
                e.includes(item.uid)
                  ? e.filter((f) => f !== item.uid)
                  : [...e, item.uid],
              )
            }
            text={collapsed && t("util:expand", { val: t("global:section") })}
            tooltipLabel={
              collapsed
                ? t("util:expand", { val: t("global:section") })
                : t("util:collapse", { val: t("global:section") })
            }
          />
        </div>
        {!collapsed && (
          <Component
            item={item}
            formik={formik}
            index={index}
            project={project}
            setProject={setProject}
            mapEditorActive={mapEditorActive}
            setMapEditorActive={setMapEditorActive}
          />
        )}
      </div>
    );
  },
);
export const AddSection = ({
  type,
  formik,
}: {
  type: NonNullable<NonNullable<Project["content"]>[number]["type"]>;
  formik: FormikProps<Project>;
}) => {
  const { t } = useTranslation();
  const { toTitleCase, toSentenceCase } = useConvertCase();
  const { title, subtitle, icon, onClick } = useSectionTypes({
    formik,
  })[type];
  const singleSections = ["calendar"];
  const disabled = //  type === "calendar" ||
    formik.values?.content?.some(
      (e) => e.type === type && singleSections.includes(type),
    );

  return (
    <Button
      className={cn(
        "bg-surface-default hover:bg-surface-highlight-subdued active:bg-surface-highlight-subdued-hovered ring-border-highlight-subdued flex h-full w-full cursor-pointer flex-col items-center gap-3 overflow-hidden rounded-2xl p-10 text-center ring-inset hover:ring-1",
        disabled && "opacity-50",
      )}
      disabled={disabled}
      disabledNotice={t("infoPage:builder.calendarSection.disabledNotice")}
      onClick={() => {
        onClick();
      }}
      classNameWrapper="h-auto"
    >
      <Icon className={cn("text-icon-default")} name={icon} />
      <span className="body-md-bold">{t("util:add", { val: title })}</span>
      <span className="text-text-subdued body-sm">{subtitle}</span>
    </Button>
  );
};

export const MainInfo = ({
  formik,
}: {
  formik: ReturnType<typeof usePageBuilderFormik>;
  mutatableMode: NonNullable<AdministratingHeaderProps["mutatableMode"]>;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const [loadingImage, setLoadingImage] = useState(false);
  const { checkFileUpload } = useVerifyFiles();
  const { organizationId, projectId } = useParams();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { metadata: imageMetadata, fetchMetadata } = useImageMetaData({
    images: formik.values.image ? [formik.values.image] : [],
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchMetadata();
  }, []);

  const imageProps = useGetFireStoreSizes(formik.values.image?.url);
  const imageRatio = useGetImageRatio(formik.values.image?.url);
  const { ImgContainer, ImgWrapper, Img } = InfoPageStyles({
    isMobile,
    full: !formik.values.image?.keepAspectRatio || isMobile,
  });

  const handleFileDrop = async (files: ImageInput) => {
    setLoadingImage(true);

    try {
      // Handle new format (array of {image, alt} objects from Unsplash/AI)
      if (Array.isArray(files) && files[0] && "url" in files[0]) {
        const imageObj = files[0] as { url: string; alt: string };
        await formik.setFieldValue("image", {
          url: imageObj.url,
          alt: imageObj.alt || "",
          keepAspectRatio: formik.values.image?.keepAspectRatio || false,
        });
        setLoadingImage(false);
        return;
      }

      // Handle file uploads (FileList or File[])
      const fileList = checkFileUpload({ files });
      if (!fileList?.length) {
        setLoadingImage(false);
        return;
      }

      const imageObjects = await getMediaObjects({
        fileList,
        setPath: () =>
          `organizationsData/${organizationId}/projects/${projectId}/images/`,
      });

      if (imageObjects?.[0]) {
        await formik.setFieldValue("image", {
          url: imageObjects[0].url,
          alt: "",
          keepAspectRatio: formik.values.image?.keepAspectRatio || false,
        });
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      // Show error toast here if you have one
    } finally {
      setLoadingImage(false);
    }
  };
  const { url, alt } = formik.values.image || {};
  const fileTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
  return (
    <div className="grid grid-cols-2 min-h-[75vh]">
      <div className="flex flex-col gap-3 p-10">
        <span className="text-text-subdued body-md-bold">
          {t("global:generalInformation")}
        </span>
        <div>
          <InfoToast
            text={t("infoPage:builder.firstSection.infoToast")}
            className="max-w-full text-sm leading-tight"
            transition={false}
          />
        </div>
        <div className="mt-10 flex flex-col gap-3">
          <OptimizedInput
            fieldPath="title"
            onFieldChange={formik.setFieldValue}
            name="title"
            className="heading-4xl"
            value={formik.values.title}
            placeholder={t("global:headline")}
            silent
            type="textarea"
            rows={1}
          />
          {/** biome-ignore lint/nursery/useUniqueElementIds: <explanation> */}
          <OptimizedInput
            fieldPath="brief"
            onFieldChange={formik.setFieldValue}
            id="brief"
            type="textarea"
            className="body-md"
            value={formik.values.brief}
            rows={5}
            // maxRows={Number.POSITIVE_INFINITY}
            placeholder={t("global:summary")}
            rich
            silent
          />
        </div>
      </div>
      <div className={cn("relative h-[75vh]", !isMobile && "desktop")}>
        <div className={ImgContainer({ className: "!w-full !h-full" })}>
          {/* {loadingImage && (
            <div
              className={cn(
                "absolute inset-0 z-10 flex items-center justify-center",
                url ? "text-white" : "text-icon-default",
              )}
            >
              <Icon name="loading" />
            </div>
          )} */}
          {url ? (
            <div
              className={ImgWrapper({
                className: cn(
                  "group/option",
                  loadingImage ? "brightness-50" : "",
                ),
              })}
              style={{
                aspectRatio: imageRatio,
              }}
            >
              <img
                {...imageProps}
                className={Img()}
                onLoad={() => setLoadingImage(false)}
                alt={alt || (url && imageMetadata[url]?.customMetadata?.alt)}
                decoding="async"
              />
              <ModifyImageButtons
                project={formik.values}
                inputRef={fileInputRef}
                imageUrl={url}
                altText={alt}
                altTextPlaceholder={imageMetadata[url]?.customMetadata?.alt}
                copyright={formik.values.image?.copyright}
                onAltTextChange={(value) => {
                  formik.setFieldValue("image.alt", value);
                }}
                onCopyrightChange={(value) => {
                  formik.setFieldValue("image.copyright", value);
                }}
                onChangeImage={handleFileDrop}
                onRemoveImage={() => {
                  formik.setFieldValue("image", null);
                }}
                onFormatChange={(value) => {
                  formik.setFieldValue("image", {
                    ...formik.values.image,
                    keepAspectRatio: !value,
                  });
                }}
                imgFormat={!formik.values.image?.keepAspectRatio}
                showFormat={true}
                maxItems={1}
                className="opacity-0 group-hover/option:opacity-100"
                saveToPath="infoPage"
                fileTypes={fileTypes}
              />
            </div>
          ) : null}
          <FileDrop
            onDrop={handleFileDrop}
            ref={fileInputRef}
            icon={loadingImage ? "loading" : !url ? "add-image" : undefined}
            className={cn("absolute inset-0 !rounded-l !rounded-r-2xl z-50")}
            hidden={!!url}
            onClick={() =>
              openModal(
                <ImagesUploadModal
                  onInputChange={handleFileDrop}
                  maxItems={1}
                  fileTypes={fileTypes}
                  stock
                  ai
                  mediaBrowser
                  basePath={
                    projectId && organizationId
                      ? `organizationsData/${organizationId}/projects/${projectId}`
                      : ""
                  }
                  saveToPath="infoPage"
                  project={formik.values}
                />,
                {
                  title: t("util:upload", {
                    val: t(getFileTypeLabel(1, fileTypes)),
                  }),
                  size: "xl",
                  transform: false,
                  style: { height: "80vh" },
                },
              )
            }
          />
        </div>
      </div>
    </div>
  );
};
